import React, { useState, useEffect } from 'react';
import {
  AlertTriangle, ClipboardCheck, TrendingUp, Shield,
  ArrowDownRight, Clock, CheckCircle,
  Calendar, Microscope, MessageSquare,
  ChevronRight, Target, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const HospitalDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/analytics/hospital-dashboard')
      .then(setStats)
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--niko-teal)]" />
      </div>
    );
  }

  if (!stats) return null;

  const quickStats = [
    { label: 'Open Safety Events', value: stats.safety_events?.open ?? 0, icon: AlertTriangle, color: 'red', href: '/hospital/safety-events' },
    { label: 'Pending Audits', value: stats.audits?.pending ?? 0, icon: ClipboardCheck, color: 'yellow', href: '/hospital/audits' },
    { label: 'Active PI Projects', value: stats.pi_projects?.active ?? 0, icon: TrendingUp, color: 'blue', href: '/hospital/pi-projects' },
    { label: 'High/Critical Risks', value: stats.risks?.high_critical ?? 0, icon: Shield, color: 'purple', href: '/hospital/risk-register' },
  ];

  const recentEvents = stats.safety_events?.recent || [];
  const upcomingAudits = stats.audits?.upcoming || [];

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 60) return `${diff} min ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  const severityDot = (sev) => {
    if (sev === 'low') return 'bg-yellow-400';
    if (sev === 'medium') return 'bg-orange-500';
    if (sev === 'high') return 'bg-red-500';
    if (sev === 'critical') return 'bg-red-700';
    return 'bg-blue-400';
  };

  const statusBadge = (s) => {
    const map = {
      open: 'bg-blue-100 text-blue-700',
      investigating: 'bg-yellow-100 text-yellow-700',
      action_required: 'bg-red-100 text-red-700',
      closed: 'bg-green-100 text-green-700',
    };
    return map[s] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6" data-testid="hospital-dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Safety & Quality Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">Real-time overview</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm" className="gap-2">
            <Calendar className="w-4 h-4" />
            {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </Button>
          <Button
            size="sm"
            className="bg-red-600 hover:bg-red-700 text-white gap-2"
            onClick={() => navigate('/hospital/safety-events')}
            data-testid="report-event-btn"
          >
            <AlertTriangle className="w-4 h-4" />
            Report Event
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {quickStats.map((stat) => (
          <a
            key={stat.label}
            href={stat.href}
            className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md transition-shadow group"
            data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                stat.color === 'red' ? 'bg-red-100' :
                stat.color === 'yellow' ? 'bg-yellow-100' :
                stat.color === 'blue' ? 'bg-blue-100' : 'bg-purple-100'
              }`}>
                <stat.icon className={`w-5 h-5 ${
                  stat.color === 'red' ? 'text-red-600' :
                  stat.color === 'yellow' ? 'text-yellow-600' :
                  stat.color === 'blue' ? 'text-blue-600' : 'text-purple-600'
                }`} />
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
          </a>
        ))}
      </div>

      {/* Main Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Safety Events */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <h2 className="font-semibold text-gray-900">Recent Safety Events</h2>
            </div>
            <a href="/hospital/safety-events" className="text-sm text-[var(--niko-teal)] font-medium hover:underline">
              View All
            </a>
          </div>
          <div className="divide-y divide-gray-100">
            {recentEvents.length === 0 ? (
              <div className="p-8 text-center text-gray-400">No recent events</div>
            ) : (
              recentEvents.map((event) => (
                <div key={event.id} className="p-4 hover:bg-gray-50 transition-colors cursor-pointer" data-testid="recent-event-item">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 ${severityDot(event.severity)}`} />
                      <div>
                        <p className="font-medium text-gray-900">{event.title}</p>
                        <p className="text-sm text-gray-500">{event.location} - {event.category}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusBadge(event.status)}`}>
                        {(event.status || '').replace('_', ' ')}
                      </span>
                      <p className="text-xs text-gray-400 mt-1">{formatTime(event.created_at)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Upcoming Audits */}
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-[var(--niko-teal)]" />
              <h2 className="font-semibold text-gray-900">Upcoming Audits</h2>
            </div>
            <a href="/hospital/audits" className="text-sm text-[var(--niko-teal)] font-medium hover:underline">
              View All
            </a>
          </div>
          <div className="p-4 space-y-3">
            {upcomingAudits.length === 0 ? (
              <div className="text-center text-gray-400 py-4">No upcoming audits</div>
            ) : (
              upcomingAudits.map((audit) => (
                <div key={audit.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer" data-testid="upcoming-audit-item">
                  <div className="w-2 h-2 rounded-full bg-gray-300" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{audit.title}</p>
                    <p className="text-xs text-gray-500">{audit.department} - {audit.type}</p>
                  </div>
                  <span className="text-xs font-medium text-gray-500">
                    {audit.scheduled_date}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Infection Summary & Ask Niko */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Infection Summary */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Microscope className="w-5 h-5 text-[var(--niko-teal)]" />
              <h2 className="font-semibold text-gray-900">Infections This Month</h2>
            </div>
            <a href="/hospital/infection-control" className="text-sm text-[var(--niko-teal)] font-medium hover:underline">
              Details
            </a>
          </div>
          <div className="flex items-center gap-8">
            <div>
              <p className="text-4xl font-bold text-gray-900" data-testid="infections-count">{stats.infections?.this_month ?? 0}</p>
              <p className="text-sm text-gray-500">Reported this month</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-gray-900" data-testid="total-events-count">{stats.safety_events?.total ?? 0}</p>
              <p className="text-sm text-gray-500">Total safety events</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-gray-900" data-testid="total-risks-count">{stats.risks?.total ?? 0}</p>
              <p className="text-sm text-gray-500">Active risks</p>
            </div>
          </div>
        </div>

        {/* Ask Niko Quick Access */}
        <div className="bg-gradient-to-br from-[var(--niko-teal-dark)] to-[var(--niko-teal)] rounded-xl p-5 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-lg">Ask Niko</h2>
              <p className="text-white/70 text-sm">PhD-Level Clinical Intelligence</p>
            </div>
          </div>
          <p className="text-white/80 text-sm mb-4">
            Get instant answers on infection prevention, patient safety, quality compliance, and clinical operations.
          </p>
          <div className="space-y-2 mb-4">
            {['CLABSI prevention bundle', 'Fall risk assessment', 'Isolation precautions'].map((q) => (
              <button key={q} className="w-full text-left px-3 py-2 bg-white/10 rounded-lg text-sm hover:bg-white/20 transition-colors">
                "{q}"
              </button>
            ))}
          </div>
          <Button className="w-full bg-white text-[var(--niko-teal)] hover:bg-white/90" data-testid="open-ask-niko-btn">
            Open Ask Niko
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HospitalDashboard;
